/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-cohort/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';

import CopyButton from '@components/core/copy-button';
import { useBoolean, useTranslation } from 'hooks';
import { StudyVisitScheduleFields, StudyCohortFields } from 'queries/generated/fragments.generated';
import { SAVE_STUDY_COHORT, GET_STUDY_COHORTS } from 'queries/study-cohorts';
import { SAVE_STUDY_VISIT_SCHEDULE } from 'queries/study-visit-schedules';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

const AddCohortDialog = ({
    cohort,
    cohorts,
    onClose,
    open,
    studyId,
    currentSchedule = null,
    isSysAdmin,
}: {
    cohort?: StudyCohortFields;
    cohorts: StudyCohortFields[];
    onClose: (any: any) => any;
    open: boolean;
    studyId: string;
    isSysAdmin: boolean;
    currentSchedule?: StudyVisitScheduleFields;
}) => {
    const initialState = {
        id: null,
        configId: null,
        name: '',
        description: '',
        subjectCode: '',
        active: true,
    };
    const initialErrors = { subjectCode: '', name: '', description: '' };
    const { t } = useTranslation();

    const [updateCohort] = useMutation(SAVE_STUDY_COHORT);
    const [updateSchedule] = useMutation(SAVE_STUDY_VISIT_SCHEDULE);
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors } as any);
    const disabledSave = useBoolean(true);

    useEffect(() => {
        if (cohort)
            setState({
                id: cohort.id,
                configId: cohort.configId,
                name: cohort.name ?? '',
                description: cohort.description ?? '',
                subjectCode: cohort.subjectCode ?? '',
                active: !!cohort.active,
            });
    }, [cohort]);

    const close = (action: string | boolean | undefined) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        }
        disabledSave.setTrue();
        onClose(action);
    };

    const validate = (value?: string, field?: string, length?: number) => {
        let error = validateFields(value, field, length);

        const configIdError = checkConfigIdErrors(state, cohorts, value);
        if (field === 'configId') {
            error = { configId: configIdError };
        }

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        const { value } = event.target;
        let length = 30;
        if (name === 'name') length = 60;
        if (name === 'description') length = 150;
        if (name === 'configId') length = 24;

        disabledSave.setFalse();

        setState({
            ...state,
            [name]: value,
        });
        validate(value, name, length);
    };

    const onCheckboxChange = (fieldName: 'active') => () => {
        disabledSave.setFalse();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const getButtonLabel = () => {
        if (currentSchedule && !cohort) return 'common:createAssign';
        return cohort ? 'update' : 'add';
    };

    const saveCohort = () => {
        disabledSave.setTrue();
        if (checkRequiredFields({ fields: ['subjectCode', 'name'], state, setErrors, errors })) {
            const isSubjectCodeExist = checkIfNameExist(
                { ...state, subjectCode: state.subjectCode.trim(), id: null },
                cohorts,
                'subjectCode',
            );
            const isNameExist = checkIfNameExist({ ...state, name: state.name.trim(), id: null }, cohorts, 'name');
            const checkCurrentSubjectCode = cohort && cohort.subjectCode !== state.subjectCode && isSubjectCodeExist;
            const checkCurrentCrfName = cohort && cohort.name !== state.name && isNameExist;

            if (
                ((isSubjectCodeExist || isNameExist) && cohort === null) ||
                checkCurrentSubjectCode ||
                checkCurrentCrfName
            ) {
                disabledSave.setTrue();
                if (checkCurrentSubjectCode || isSubjectCodeExist)
                    setErrors({ ...errors, subjectCode: t('admin:cohortSubjectCodeAlreadyExists') });

                if (checkCurrentCrfName || isNameExist)
                    setErrors({ ...errors, name: t('admin:cohortNameAlreadyExists') });

                return;
            }

            const submitData = {
                studyId,
                cohort: {
                    id: state.id,
                    configId: state.configId,
                    name: state.name,
                    description: state.description,
                    subjectCode: state.subjectCode,
                    active: state.active,
                },
                reason: '',
            };

            if (currentSchedule) {
                updateCohort({
                    variables: {
                        ...submitData,
                    },
                })
                    .then((res: any) => {
                        const ids = currentSchedule.cohortIds.concat(res.data.upsertCohort.id);
                        updateSchedule({
                            variables: {
                                studyId,
                                visitSchedule: {
                                    id: currentSchedule?.id ?? null,
                                    name: currentSchedule.name,
                                    description: currentSchedule.description,
                                    active: currentSchedule.active,
                                    treatmentArmIds: currentSchedule?.treatmentArmIds ?? [],
                                    cohortIds: ids,
                                },
                                reason: '',
                            },
                            refetchQueries: [
                                {
                                    query: GET_STUDY_COHORTS,
                                    variables: { studyId },
                                },
                            ],
                        });
                    })
                    .then(() => {
                        close('add');
                    })
                    .catch(() => {
                        close('error');
                    });
            } else {
                updateCohort({
                    variables: {
                        ...submitData,
                    },
                    refetchQueries: [
                        {
                            query: GET_STUDY_COHORTS,
                            variables: {
                                studyId,
                            },
                        },
                    ],
                })
                    .then(() => {
                        close(cohort ? 'edit' : 'add');
                    })
                    .catch(() => {
                        close('error');
                    });
            }
        }
    };

    return (
        <CustomDialog
            className="create-controls-dialog"
            onClose={close}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
        >
            <DialogTitle id="add-critical-variable-dialog">
                {cohort ? `${t('admin:editCohort')}: ${cohort?.recordId}` : t('admin:addCohort')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close(false);
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent style={{ padding: 20, paddingTop: 15, paddingBottom: 0 }}>
                <div className="checkboxes-wrapper">
                    <div className="left-part mb-15">
                        <Checkbox
                            rounded="none"
                            id="cohort-active-checkbox"
                            className="cell-checkbox no-border mr"
                            checked={state.active}
                            onChange={onCheckboxChange('active')}
                            label={t('common:active')}
                            disabled={isSysAdmin}
                            data-testid="cohort-active-checkbox"
                            size="medium"
                        />
                    </div>
                </div>
                <div style={{ alignItems: 'flex-start' }} className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            label={t('admin:sourceSystemId')}
                            id="edit-cohort-subject-code"
                            fullWidth
                            required
                            value={state.subjectCode}
                            onChange={onChange('subjectCode')}
                            placeholder={t('admin:enterSourceSystemId')}
                            helperText={
                                <CharactersRemaining
                                    id="create-cohort-dialog-subject-code"
                                    length={state.subjectCode.length}
                                    max={30}
                                    error={errors.subjectCode}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.subjectCode}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            label={t('name')}
                            id="edit-cohort-name"
                            fullWidth
                            required
                            value={state.name}
                            onChange={onChange('name')}
                            placeholder={t('admin:enterName')}
                            helperText={
                                <CharactersRemaining
                                    id="create-cohort-dialog-name"
                                    length={state.name?.length}
                                    max={60}
                                    error={errors.name}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.name}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                </div>
                <TextField
                    label={`${t('description')}`}
                    id="edit-cohort-description"
                    multiline
                    rows={5}
                    fullWidth
                    value={state.description}
                    onChange={onChange('description')}
                    placeholder={t('ra:criticalVariableDescriptionHelperText')}
                    helperText={
                        <CharactersRemaining
                            id="create-cohort-dialog-description"
                            length={state.description.length}
                            max={150}
                            error={errors.description}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    error={!!errors.description}
                    className="before-select mb"
                    disabled={isSysAdmin}
                />
                {!!cohort && (
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type">
                            <TextField
                                id="edit-field-config-id"
                                label={t('admin:configId')}
                                onChange={onChange('configId')}
                                fullWidth
                                value={state.configId}
                                size="small"
                                helperText={
                                    <span style={{ color: '#ae0015' }} className="remaining-text">
                                        {errors.configId}
                                    </span>
                                }
                                slotProps={{
                                    input: {
                                        readOnly: !isSysAdmin,
                                        endAdornment: <CopyButton value={state.configId ?? ''} />,
                                    },
                                }}
                                variant="outlined"
                                color="secondary"
                                error={!!errors.configId}
                            />
                        </FormControl>
                    </div>
                )}
            </DialogContent>
            <DialogActions
                style={{ paddingRight: 20, paddingBottom: 20, paddingLeft: 20 }}
                className={`actions-info-wrapper-edit-cv ${!cohort ? 'flex-end' : ''}`}
            >
                <ModalUpdatedInfo item={cohort} />
                <Button
                    disabled={Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledSave.value}
                    text={getButtonLabel()}
                    id="edit-cohort-save-close"
                    onClick={saveCohort}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddCohortDialog;
